import { useMemo, useRef, useState } from "react";
import { Plus, PencilSimple, Trash, MapPin } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const LETTERS = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ"];
const letterOf = (p) => {
  const c = (p.sort_name || p.street || "").charAt(0).toUpperCase();
  return /[A-Z]/.test(c) ? c : "#";
};

export function PropertiesPane({ properties, searchActive, selectedId, onSelect, onAdd, onEdit, onDelete, canDelete, loading }) {
  const [filter, setFilter] = useState("");
  const listRef = useRef(null);
  const shown = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return q ? properties.filter((p) => `${p.street} ${p.city} ${p.zip}`.toLowerCase().includes(q)) : properties;
  }, [properties, filter]);
  const present = new Set(shown.map(letterOf));

  const jump = (L) => {
    const el = listRef.current?.querySelector(`[data-letter="${L}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  let lastLetter = null;
  return (
    <div className="h-full flex flex-col bg-white" data-testid="service-pane-properties">
      <div className="px-3 pt-3 pb-2 border-b border-slate-200 space-y-2">
        <div className="flex items-center justify-between">
          <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500">Properties</div>
          <Button size="sm" onClick={onAdd} className="h-7 bg-[#327de3] hover:bg-[#1f64c2] text-white" data-testid="service-add-address-btn">
            <Plus size={13} weight="bold" className="mr-1" /> Add Address
          </Button>
        </div>
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter addresses…" className="h-8 text-sm" data-testid="service-property-filter" />
      </div>
      <div className="flex-1 min-h-0 flex">
        <div ref={listRef} className="flex-1 overflow-y-auto py-1" data-testid="service-property-list">
          {loading && <div className="p-4 text-sm text-slate-400">Loading…</div>}
          {!loading && shown.length === 0 && (
            <div className="p-5 text-sm text-slate-500 text-center" data-testid="service-properties-empty">
              <MapPin size={28} className="mx-auto mb-2 text-slate-300" />
              {searchActive ? "No properties match your search." : properties.length ? "No matching addresses." : "No addresses yet. Click “Add Address” to start."}
            </div>
          )}
          {shown.map((p) => {
            const L = letterOf(p);
            const header = L !== lastLetter;
            lastLetter = L;
            const sel = p.id === selectedId;
            return (
              <div key={p.id} data-letter={header ? L : undefined}>
                {header && <div className="px-3 pt-2 pb-0.5 text-[10px] font-bold text-slate-400 tracking-widest">{L}</div>}
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelect(p)}
                  onKeyDown={(e) => { if (e.key === "Enter") onSelect(p); }}
                  className={`group mx-1.5 px-2.5 py-2 rounded-md cursor-pointer flex items-start gap-2 transition-colors ${sel ? "bg-[#eaf2ff] shadow-[inset_3px_0_0_#327de3]" : "hover:bg-slate-50"}`}
                  data-testid={`service-property-${p.id}`}
                  data-selected={sel ? "true" : undefined}
                >
                  <MapPin size={15} weight={sel ? "fill" : "regular"} className={`mt-0.5 flex-shrink-0 ${sel ? "text-[#327de3]" : "text-slate-400"}`} />
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold truncate ${sel ? "text-[#1f64c2]" : "text-slate-800"}`}>{p.street}</div>
                    {(p.city || p.state) && <div className="text-[11px] text-slate-500 truncate">{[p.city, p.state].filter(Boolean).join(", ")} {p.zip}</div>}
                  </div>
                  <span className="text-[10px] text-slate-400 font-semibold mt-0.5" data-testid={`service-property-count-${p.id}`}>{p.record_count || 0}</span>
                  <div className="hidden group-hover:flex items-center gap-0.5">
                    <button type="button" onClick={(e) => { e.stopPropagation(); onEdit(p); }} className="p-1 rounded hover:bg-slate-200 text-slate-500" title="Edit" data-testid={`service-property-edit-${p.id}`}>
                      <PencilSimple size={12} />
                    </button>
                    {canDelete && (
                      <button type="button" onClick={(e) => { e.stopPropagation(); onDelete(p); }} className="p-1 rounded hover:bg-rose-100 text-rose-500" title="Delete" data-testid={`service-property-delete-${p.id}`}>
                        <Trash size={12} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="w-5 flex-shrink-0 flex flex-col items-center py-1 overflow-y-auto border-l border-slate-100" data-testid="service-alpha-nav">
          {LETTERS.map((L) => (
            <button
              key={L}
              type="button"
              disabled={!present.has(L)}
              onClick={() => jump(L)}
              className={`text-[9px] leading-[13px] font-bold w-full ${present.has(L) ? "text-[#327de3] hover:text-[#1f64c2]" : "text-slate-300 cursor-default"}`}
              data-testid={`service-alpha-${L}`}
            >
              {L}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
